//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { SamplingCampaignRequestCountry } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { CampaignRequest } from '@apis/campaign-requests/shared/CampaignRequests';
import { CAMPAIGN_REQUEST_FORM_VALIDATION } from '@campaigns/constants/CampaignRequestFormValidationConstants';
import { CAMPAIGN_REQUEST_LOCALES } from '@campaigns/constants/CampaignRequestsLocaleConstants';
import { CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/CampaignsTrackingId';
import Icon from '@components/atoms/icon/Icon';
import { BodyText, CaptionErrorText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import DropdownSelect, {
  SelectItemValue,
} from '@components/molecules/dropdown-select/DropdownSelect';
import { isDefined } from '@utils/filterPredicates';

export interface CampaignRequestLocaleFieldProps {
  availableCountryCodes?: SamplingCampaignRequestCountry[];
  shouldHideLabel?: boolean;
}

export const CampaignRequestLocaleField = ({
  availableCountryCodes,
  shouldHideLabel = false,
}: CampaignRequestLocaleFieldProps) => {
  const { control, setValue } = useFormContext<CampaignRequest>();

  const getFilteredLocales = () => {
    if (
      !isDefined(availableCountryCodes) ||
      availableCountryCodes.length === 0
    ) {
      return CAMPAIGN_REQUEST_LOCALES;
    }

    return CAMPAIGN_REQUEST_LOCALES.filter((locale) =>
      availableCountryCodes.includes(locale.country),
    );
  };

  const filteredLocales = getFilteredLocales();
  const shouldValidateLocale =
    isDefined(availableCountryCodes) && availableCountryCodes.length > 0;

  return (
    <div>
      {!shouldHideLabel && <BodyText>Locale</BodyText>}
      <FormFieldController
        control={control}
        name='locale'
        rules={{
          ...(CAMPAIGN_REQUEST_FORM_VALIDATION.locale ?? {}),
          required: {
            message: 'Please select a locale',
            value: true,
          },
          ...(shouldValidateLocale && {
            validate: (value) => {
              const isValid = filteredLocales.some(
                (locale) => locale.value === value,
              );
              return isValid || 'Please select a valid locale';
            },
          }),
        }}
      >
        {(props) => {
          const handleSelect = (localeValue: SelectItemValue) => {
            // Update the locale value
            props.onChange(localeValue);
            // Find the selected locale object
            const selectedLocale = CAMPAIGN_REQUEST_LOCALES.find(
              (locale) => locale.value === localeValue,
            );
            if (isDefined(selectedLocale)) {
              // Update the audienceDetails.country with the corresponding country
              setValue('audienceDetails.country', selectedLocale.country);
            }
          };

          return (
            <>
              <StyledDropdownSelect
                handleSelect={handleSelect}
                items={filteredLocales}
                trackingId={CAMPAIGNS_TRACKING_ID.CREATE_CAMPAIGN_LOCALE_INPUT}
                value={props.value}
              />
              {props.error && (
                <ErrorText>
                  <StyledIcon name='error' />
                  <CaptionErrorText>{props.error.message}</CaptionErrorText>
                </ErrorText>
              )}
            </>
          );
        }}
      </FormFieldController>
    </div>
  );
};

const StyledDropdownSelect = styled(DropdownSelect)`
  margin-top: ${({ theme }) => theme.spacings[1]};
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacings[2]};
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
